import api from './api';

export const adminService = {
  /**
   * Create new user (Admin only)
   * @param {Object} userData - User data
   * @returns {Promise<Object>}
   */
  createUser: async (userData) => {
    const response = await api.post('/admin/create-user', userData);
    return response.data;
  },

  /**
   * Get all users with filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>}
   */
  getAllUsers: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.role) queryParams.append('role', params.role);
    if (params.search) queryParams.append('search', params.search);

    const response = await api.get(`/admin/users?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Update user status (activate/deactivate)
   * @param {string} userId - User ID
   * @param {boolean} isActive - New active status
   * @returns {Promise<Object>}
   */
  updateUserStatus: async (userId, isActive) => {
    const response = await api.patch(`/admin/users/${userId}/status`, { isActive });
    return response.data;
  },

  /**
   * Get user details (Admin only)
   * @param {string} userId - User ID
   * @returns {Promise<Object>}
   */
  getUserDetails: async (userId) => {
    const response = await api.get(`/admin/user/${userId}`);
    return response.data;
  },

  /**
   * Update HR/HRO user details (Admin only)
   * @param {string} userId - User ID
   * @param {Object} payload - Updated detail fields
   * @returns {Promise<Object>}
   */
  updateUserDetails: async (userId, payload) => {
    const isFormDataPayload = typeof FormData !== 'undefined' && payload instanceof FormData;
    const response = await api.patch(
      `/admin/users/${userId}/details`,
      payload,
      isFormDataPayload
        ? {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
        : undefined
    );
    return response.data;
  },

  /**
   * Get user update approval requests (Chairman only)
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>}
   */
  getUserUpdateApprovals: async (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);

    const response = await api.get(`/admin/user-update-approvals?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Review user update approval request (Chairman only)
   * @param {string} requestId - Approval request ID
   * @param {string} action - approve or reject
   * @param {string} notes - Optional review notes
   * @returns {Promise<Object>}
   */
  reviewUserUpdateApproval: async (requestId, action, notes = '') => {
    const response = await api.patch(`/admin/user-update-approvals/${requestId}/review`, {
      action,
      notes
    });
    return response.data;
  },

  /**
   * Get office IP allowlist (Admin only)
   * @returns {Promise<Object>}
   */
  getOfficeIps: async () => {
    const response = await api.get('/admin/security/office-ips');
    return response.data;
  },

  /**
   * Update office IP allowlist (Admin only)
   * @param {string[]} officeIps - Allowed public IP list
   * @returns {Promise<Object>}
   */
  updateOfficeIps: async (officeIps) => {
    const response = await api.patch('/admin/security/office-ips', { officeIps });
    return response.data;
  },

  /**
   * Reset bound device for HR/HRO user (Admin only)
   * @param {string} userId - User document id
   * @returns {Promise<Object>}
   */
  resetUserDeviceBinding: async (userId) => {
    const response = await api.patch(`/admin/users/${userId}/reset-device`);
    return response.data;
  },

  /**
   * Get all HR/HRO users (Admin only)
   * @param {string} role - Optional role filter (HR or HRO)
   * @returns {Promise<Object>}
   */
  getHRUsers: async (role = '') => {
    const queryParams = role ? `?role=${role}` : '';
    const response = await api.get(`/logs/hr-users${queryParams}`);
    return response.data;
  }
};

export default adminService;